/**
 * 실시간 주차 관제 — 관제 콘솔 셸(헤더 + CCTV 레일 + 도면/영상 스테이지 + 인스펙터).
 *
 * 이전 구조는 Paper/Card 세로 스택이라 도면(420px)과 영상(800px 고정 카드)이
 * 따로 떨어져 스크롤로 오가야 했다. 지금은 왼쪽 레일(CCTV 목록) · 가운데
 * 스테이지(영상) · 오른쪽 패널(현장 도면) 로 나눠, 도면 마커를 누르면 가운데
 * 영상이 바로 바뀐다.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Alert,
  Snackbar,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import SettingsIcon from '@mui/icons-material/Settings';
import VideocamIcon from '@mui/icons-material/Videocam';
import '../../index.css';

import { RealtimeParkingViewProps, ModalImageData } from './types';
import SiteMapCanvas from '../../components/SiteMap/SiteMapCanvas';
import { useSiteMap } from '../../components/SiteMap/useSiteMap';
import ConsoleShell from '../../components/console/ConsoleShell';
import { consoleTokens } from '../../styles/consoleTokens';
import { SimulatorService } from '../../services/SimulatorService';
import { SimulatorMap } from '../../models/Simulator';
import { useRealtimeSettings } from './hooks/useRealtimeSettings';
import { useRealtimeCctvTemplate } from './hooks/useRealtimeCctvTemplate';
import { useRealtimeImageRefresh } from './hooks/useRealtimeImageRefresh';
import CctvListSidebar from './components/CctvListSidebar';
import CctvImageStage from './components/CctvImageStage';
import RealtimeControlPanel from './components/RealtimeControlPanel';
import RealtimeImageModal from './components/RealtimeImageModal';

export type { RealtimeParkingViewProps } from './types';

const RealtimeParkingView: React.FC<RealtimeParkingViewProps> = ({ project, onBack: _onBack }) => {
  const theme = useTheme();
  const t = consoleTokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('lg'));

  const projectId = project?.id || window.location.pathname.split('/')[2] || '290';

  // UI state
  const [isRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageSavingEnabled, setImageSavingEnabled] = useState(true);
  const [modalImage, setModalImage] = useState<ModalImageData | null>(null);
  const [loadingCctvImages] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // 셸 pane 열림 상태
  const [railOpen, setRailOpen] = useState(true);
  // 도면은 기본으로 펼침 — 마커 클릭이 주 동선이다.
  const [inspectorOpen, setInspectorOpen] = useState(true);

  // 맵 기반 CCTV 목록 — 하드코딩 템플릿 대신 맵에 배치된 CCTV 를 출처로 쓴다.
  const [maps, setMaps] = useState<SimulatorMap[]>([]);
  const [selectedMapId, setSelectedMapId] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const uploadedUrlRef = useRef<string | null>(null);

  const {
    settings,
    availableFolders,
    loadAvailableFolders,
    handleSettingChange,
  } = useRealtimeSettings(projectId);

  const {
    cctvTemplate,
    cctvList,
    selectedCctv,
    selectedCctvImages,
    setSelectedCctvImages,
    cctvImageError,
    setCctvImageError,
    imageUpdateKey,
    setImageUpdateKey,
    templateBasedMode,
    loadCctvTemplate,
    handleCctvSelect,
    applyMapCctvs,
    refreshImages,
  } = useRealtimeCctvTemplate(projectId);

  useRealtimeImageRefresh({
    projectId,
    templateBasedMode,
    selectedCctv,
    cctvTemplate,
    isRunning,
    selectedCctvImages,
    setSelectedCctvImages,
    setCctvImageError,
    setImageUpdateKey,
    modalImage,
    setModalImage,
    autoRefresh,
  });

  // 이미지가 실제로 교체된 시각 — 헤더의 "갱신 hh:mm:ss" 표기용.
  useEffect(() => {
    if (imageUpdateKey > 0) setLastUpdated(new Date());
  }, [imageUpdateKey]);

  useEffect(() => {
    loadAvailableFolders();
    loadCctvTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // 맵 목록 로드
  useEffect(() => {
    const siteProjectId = Number(projectId);
    if (!Number.isFinite(siteProjectId)) return;
    let cancelled = false;
    SimulatorService.listMaps(siteProjectId)
      .then((list) => !cancelled && setMaps(list))
      .catch(() => !cancelled && setMaps([]));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // 맵 도면 + 맵에 배치된 CCTV — offline-manage 와 같은 방식으로 그린다.
  const selectedMapName = maps.find((m) => m.id === selectedMapId)?.name || '';
  const siteMap = useSiteMap(selectedMapId, selectedMapName);
  useEffect(() => {
    if (siteMap.cameras.length > 0) {
      applyMapCctvs(siteMap.cameras.map(({ name, ip }) => ({ name, ip })), selectedMapName || '맵');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteMap.cameras]);

  const releaseUploaded = () => {
    if (uploadedUrlRef.current) {
      URL.revokeObjectURL(uploadedUrlRef.current);
      uploadedUrlRef.current = null;
    }
  };

  const handleRefresh = () => {
    releaseUploaded();
    refreshImages();
  };

  /** 업로드/드롭한 이미지를 현재 CCTV 자리에 띄운다 — 실시간 피드 없이 한 장으로 작업할 때. */
  const applyLocalImage = (file: File) => {
    releaseUploaded();
    const url = URL.createObjectURL(file);
    uploadedUrlRef.current = url;
    setAutoRefresh(false);
    setSelectedCctvImages({ original: url, roi_result: url });
    setImageUpdateKey((prev) => prev + 1);
    setCctvImageError(null);
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) applyLocalImage(file);
  };

  useEffect(() => releaseUploaded, []);

  const selectedConfig = cctvTemplate?.cctvList.find((c) => c.cctvId === selectedCctv) || null;

  // API 모드는 cctvList(string[]) 만 있어 레일이 받는 CctvConfig 로 어댑팅한다.
  const railItems = useMemo(
    () => cctvTemplate?.cctvList
      ?? cctvList.map((id) => ({ cctvId: id, displayName: id, description: '', images: [] })),
    [cctvTemplate, cctvList],
  );

  const header = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <VideocamIcon sx={{ fontSize: 18, color: t.accent }} />
      <Typography sx={{ fontWeight: 700, fontSize: 14, mr: 0.5 }}>실시간 주차 관제</Typography>

      <Select
        size="small"
        displayEmpty
        value={selectedMapId === null ? '' : String(selectedMapId)}
        onChange={(e) => setSelectedMapId(e.target.value ? Number(e.target.value) : null)}
        sx={{ minWidth: 200, height: 32, fontSize: 13 }}
      >
        <MenuItem value=""><em>맵 없이 (기존 템플릿)</em></MenuItem>
        {maps.map((m) => (
          <MenuItem key={m.id} value={String(m.id)}>{m.name}</MenuItem>
        ))}
      </Select>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: t.line }} />

      <Typography sx={{ fontSize: 12, color: t.sub }}>
        CCTV {railItems.length}대
      </Typography>
      <Typography sx={{ fontSize: 12, color: lastUpdated ? t.ok : t.faint, fontFamily: t.mono }}>
        {lastUpdated ? `갱신 ${lastUpdated.toLocaleTimeString('ko-KR', { hour12: false })}` : '갱신 없음'}
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
        새로고침
      </Button>
      <Button size="small" variant="outlined" component="label" startIcon={<UploadIcon />}>
        이미지 업로드
        <input type="file" hidden accept="image/*" onChange={handleUpload} />
      </Button>
      <FormControlLabel
        sx={{ mr: 0 }}
        control={<Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
        label={<Typography sx={{ fontSize: 12 }}>자동 갱신 60초</Typography>}
      />
      <Tooltip title="모니터링 설정">
        <IconButton size="small" onClick={() => setSettingsDialogOpen(true)}>
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  // 오른쪽 패널 = 현장 도면. 마커를 누르면 가운데 영상이 그 카메라로 바뀐다.
  const mapPane = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, p: 0.75 }}>
      {selectedMapId === null ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
          <Typography sx={{ fontSize: 13, color: t.faint, textAlign: 'center' }}>
            위에서 맵을 고르면 도면이 표시됩니다.
          </Typography>
        </Box>
      ) : (
        <>
          {siteMap.error && <Alert severity="warning" sx={{ mb: 0.75 }}>{siteMap.error}</Alert>}
          <SiteMapCanvas
            levels={siteMap.levels}
            selectedCctv={selectedCctv}
            onCctvClick={(cam) => handleCctvSelect(cam.name)}
            height="100%"
          />
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', minHeight: 0 }}>
      <ConsoleShell
        header={header}
        rail={<CctvListSidebar cctvList={railItems} selectedCctv={selectedCctv} onSelect={handleCctvSelect} />}
        railOpen={railOpen}
        onToggleRail={() => setRailOpen((v) => !v)}
        inspector={mapPane}
        inspectorOpen={inspectorOpen}
        onToggleInspector={() => setInspectorOpen((v) => !v)}
        inspectorWidth={isNarrow ? 320 : 460}
      >
        <CctvImageStage
          cctv={selectedConfig}
          selectedCctv={selectedCctv}
          images={selectedCctvImages}
          imageUpdateKey={imageUpdateKey}
          loading={loadingCctvImages}
          error={cctvImageError}
          onZoom={(src, title, alt) => setModalImage({ src, title, alt })}
          onDropFile={applyLocalImage}
        />
      </ConsoleShell>

      <RealtimeControlPanel
        settingsDialogOpen={settingsDialogOpen}
        setSettingsDialogOpen={setSettingsDialogOpen}
        settings={settings}
        availableFolders={availableFolders}
        handleSettingChange={handleSettingChange}
        isRunning={isRunning}
        imageSavingEnabled={imageSavingEnabled}
        setImageSavingEnabled={setImageSavingEnabled}
        isMobile={isMobile}
      />

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <RealtimeImageModal
        modalImage={modalImage}
        imageUpdateKey={imageUpdateKey}
        onClose={() => setModalImage(null)}
      />
    </Box>
  );
};

export default RealtimeParkingView;
